<template>
  <div class="so-version-panel" v-loading="loading">
    <el-alert
      v-if="localOnly"
      title="SO 版本已开启“不同步对端”"
      description="版本、参数、SO 和 SN 的新增、修改与删除只操作当前服务器。"
      type="warning"
      show-icon
      :closable="false"
    />
    <el-card class="workspace-card current-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>统一项版本设置</span>
          <div class="header-actions">
            <el-switch v-model="localOnly" :disabled="saving" active-text="不同步对端" />
            <el-radio-group v-model="soDiagId" size="small" class="so-diag-switch">
              <el-radio-button
                v-for="item in soDiagOptions"
                :key="item.value"
                :value="item.value"
              >{{ item.label }}</el-radio-button>
            </el-radio-group>
            <el-button :loading="loading" @click="loadSoVersionInfo">刷新</el-button>
          </div>
        </div>
      </template>

      <div class="summary-strip version-summary">
        <div class="summary-item">
          <span class="summary-label">当前类型</span>
          <strong>{{ selectedSoDiagLabel }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">正式版本</span>
          <strong>{{ officialVersion || '-' }}</strong>
        </div>
        <div class="summary-item">
          <span class="summary-label">测试版本</span>
          <strong>{{ testVersion || '-' }}</strong>
        </div>
      </div>

      <el-form class="current-form" label-position="top">
        <el-row :gutter="16">
          <el-col :xs="24" :md="8">
            <el-form-item label="当前正式版本">
              <el-input v-model="editOfficialVersion" placeholder="V44.40" maxlength="6" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="当前测试版本">
              <el-input v-model="editTestVersion" placeholder="V44.40" maxlength="6" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8" class="version-actions-col">
            <el-button type="primary" :loading="saving" @click="saveCurrentVersions">保存当前版本</el-button>
            <el-button type="success" plain @click="openAddVersionDialog">新增测试版本</el-button>
          </el-col>
        </el-row>
      </el-form>
      <div class="version-tip">版本格式固定为 Vxx.xx；保存时后端会验证测试版本必须大于或等于正式版本。</div>
    </el-card>

    <el-card class="workspace-card manage-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>{{ selectedSoDiagLabel }} 参数与 SO 版本管理</span>
          <div class="header-actions">
            <el-button :loading="loading" @click="loadSoVersionInfo">刷新数据</el-button>
          </div>
        </div>
      </template>

      <el-empty v-if="!versions.length" description="暂无版本数据，请先新增测试版本" />
      <template v-else>
        <el-tabs v-model="currentVersion" type="card" class="version-tabs">
          <el-tab-pane v-for="version in versions" :key="version.name" :label="version.name" :name="version.name" />
        </el-tabs>

        <div class="active-key-line">
          当前版本：<strong>{{ currentVersion }}</strong>
        </div>

        <div class="manage-grid">
          <section class="sub-card">
            <div class="sub-header">
              <div>
                <h3>参数管理</h3>
                <p>参数值统一使用 TRUE / FALSE，下拉选择保存。</p>
              </div>
              <el-button type="primary" :disabled="!currentVersion" @click="openAddParamDialog">新增参数</el-button>
            </div>

            <el-table :data="currentParams" border stripe class="data-table" empty-text="暂无参数，可点击右上角新增参数">
              <el-table-column prop="key" label="参数名称" min-width="170" />
              <el-table-column label="参数值" min-width="260">
                <template #default="scope">
                  <div class="bool-editor">
                    <el-select v-model="scope.row.value" size="small">
                      <el-option label="TRUE" value="TRUE" />
                      <el-option label="FALSE" value="FALSE" />
                    </el-select>
                    <el-button size="small" type="primary" plain @click="saveParam(scope.row)">保存</el-button>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="110" align="center">
                <template #default="scope">
                  <el-button size="small" type="danger" @click="deleteParam(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>

          <section class="sub-card">
            <div class="sub-header">
              <div>
                <h3>SO 版本管理</h3>
                <p>SO 名称和版本值可自由编辑保存。</p>
              </div>
              <el-button type="primary" :disabled="!currentVersion" @click="openAddSoDialog">新增 SO</el-button>
            </div>

            <el-table :data="currentSoItems" border stripe class="data-table" empty-text="暂无 SO，可点击右上角新增 SO">
              <el-table-column prop="key" label="SO" min-width="210" />
              <el-table-column label="版本" min-width="220">
                <template #default="scope">
                  <el-input v-model="scope.row.value" size="small">
                    <template #append>
                      <el-button @click="saveSoItem(scope.row)">保存</el-button>
                    </template>
                  </el-input>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="110" align="center">
                <template #default="scope">
                  <el-button size="small" type="danger" @click="deleteSoItem(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>

          <section class="sub-card sn-card">
            <div class="sub-header">
              <div>
                <h3>SN 管理</h3>
                <p>当前版本允许下发 SO 的 SN 列表，可添加或删除。</p>
              </div>
              <el-button type="primary" :disabled="!currentVersion" @click="openAddSnDialog">新增 SN</el-button>
            </div>

            <el-table :data="currentSnItems" border stripe class="data-table" empty-text="暂无 SN，可点击右上角新增 SN">
              <el-table-column prop="value" label="SN" min-width="220">
                <template #default="scope">
                  <span>{{ scope.row.sn || scope.row.value }}</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="110" align="center">
                <template #default="scope">
                  <el-button size="small" type="danger" @click="deleteSn(scope.row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>
        </div>
      </template>
    </el-card>

    <el-dialog v-model="addVersionDialogVisible" title="新增测试版本" width="520px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="新版本">
          <el-input v-model="newVersionForm.version" placeholder="例如 V44.40" maxlength="6" />
        </el-form-item>
        <el-form-item label="复制来源版本">
          <el-select v-model="newVersionForm.copyFromVersion" placeholder="不选择则创建默认参数和默认 SO" clearable style="width: 100%;">
            <el-option v-for="item in versionList" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <div class="dialog-note">
          默认参数：AllSn / Test_envi / DeleteOL = FALSE<br />
          默认 SO：{{ defaultSoNames.length ? defaultSoNames.join('、') : 'libMAZDA_OLFUNC.lol、libMAZDA_SPEC_OLFUNC.lol、libONLINEMAZDA_ADAS_OLFUNC.lol' }}<br />
          默认 SO 初始值：{{ defaultSoValue }}
        </div>
        <el-form-item label="创建后设置为当前测试版本">
          <el-switch v-model="newVersionForm.setAsTest" active-text="是" inactive-text="否" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addVersionDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="saving" @click="submitAddVersion">提交</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-model="addParamDialogVisible" title="添加参数" width="520px" destroy-on-close>
      <el-form label-position="top">
        <el-alert
          title="参数会新增到当前选中的版本下，参数值只能是 TRUE 或 FALSE"
          type="info"
          :closable="false"
          class="dialog-alert"
        />
        <el-form-item label="当前版本">
          <el-input :model-value="currentVersion" disabled />
        </el-form-item>
        <el-form-item label="参数名称">
          <el-input v-model="newParamForm.key" placeholder="例如 CustomParam" />
        </el-form-item>
        <el-form-item label="参数值">
          <el-select v-model="newParamForm.value" style="width: 100%;">
            <el-option label="FALSE" value="FALSE" />
            <el-option label="TRUE" value="TRUE" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addParamDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="saving" @click="submitAddParam">提交</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-model="addSoDialogVisible" title="新增 SO 名称" width="520px" destroy-on-close>
      <el-form label-position="top">
        <el-alert
          title="SO 会新增到当前选中的版本里，版本值按字符串保存"
          type="info"
          :closable="false"
          class="dialog-alert"
        />
        <el-form-item label="当前版本">
          <el-input :model-value="currentVersion" disabled />
        </el-form-item>
        <el-form-item label="SO 名称">
          <el-input v-model="newSoForm.key" placeholder="例如 libMAZDA_OLFUNC.lol" />
        </el-form-item>
        <el-form-item label="版本值">
          <el-input v-model="newSoForm.value" :placeholder="defaultSoValue || '例如 0x26052001'" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addSoDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="saving" @click="submitAddSo">提交</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog v-model="addSnDialogVisible" title="新增 SN" width="520px" destroy-on-close>
      <el-form label-position="top">
        <el-alert
          title="SN 会新增到当前选中版本的 SN 列表中"
          type="info"
          :closable="false"
          class="dialog-alert"
        />
        <el-form-item label="当前版本">
          <el-input :model-value="currentVersion" disabled />
        </el-form-item>
        <el-form-item label="SN">
          <el-input v-model="newSnForm.sn" placeholder="例如 985190000034" clearable />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addSnDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="saving" @click="submitAddSn">提交</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useMazdaSoVersion } from '@/composables/mazda/useMazdaSoVersion';

const props = defineProps({
  mazdaPost: { type: Function, required: true },
  serverName: { type: String, default: '' },
});

const {
  loading,
  saving,
  localOnly,
  soDiagId,
  soDiagOptions,
  selectedSoDiagLabel,
  versions,
  versionList,
  currentVersion,
  officialVersion,
  testVersion,
  editOfficialVersion,
  editTestVersion,
  defaultSoNames,
  defaultSoValue,
  addVersionDialogVisible,
  addParamDialogVisible,
  addSoDialogVisible,
  addSnDialogVisible,
  newVersionForm,
  newParamForm,
  newSoForm,
  newSnForm,
  currentParams,
  currentSoItems,
  currentSnItems,
  loadSoVersionInfo,
  saveCurrentVersions,
  openAddVersionDialog,
  submitAddVersion,
  openAddParamDialog,
  saveParam,
  submitAddParam,
  deleteParam,
  openAddSoDialog,
  saveSoItem,
  submitAddSo,
  deleteSoItem,
  openAddSnDialog,
  submitAddSn,
  deleteSn,
} = useMazdaSoVersion(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.so-version-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.workspace-card {
  border-radius: 22px;
  border: 1px solid rgba(226, 233, 243, 0.95);
  box-shadow: 0 16px 36px rgba(26, 46, 79, 0.08);
}

.card-header,
.header-actions,
.version-actions-col,
.bool-editor {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header {
  justify-content: space-between;
}

.so-diag-switch {
  margin-right: 2px;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
  max-width: 960px;
}

.summary-item {
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7faff, #f3f7ff);
  border: 1px solid #e8eef8;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.summary-item.wide {
  grid-column: span 2;
}

.summary-label,
.version-tip,
.active-key-line,
.sub-header p {
  color: #7c8798;
  font-size: 13px;
}

.summary-item strong,
.active-key-line strong {
  color: #1f2d3d;
  word-break: break-all;
}

.version-actions-col {
  padding-top: 30px;
  flex-wrap: wrap;
}

.version-tip {
  line-height: 1.7;
  margin-top: 14px;
  padding: 9px 12px;
  border: 1px solid rgba(230, 162, 60, 0.28);
  border-radius: 10px;
  background: var(--wf-status-warning-bg);
  color: var(--wf-status-warning-text);
}

.version-tabs {
  margin-bottom: 12px;
}

.active-key-line {
  padding: 10px 12px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #f8fbff;
  border: 1px solid #e8eef8;
  word-break: break-all;
}

.manage-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(360px, 1fr));
  gap: 16px;
}

.sub-card {
  padding: 16px;
  border: 1px solid #e8eef8;
  border-radius: 18px;
  background: #ffffff;
}

.sn-card {
  grid-column: 1 / -1;
}

.sub-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.sub-header .el-button {
  flex-shrink: 0;
  margin-top: 2px;
}

.sub-header h3 {
  margin: 0 0 6px;
  font-size: 17px;
  color: #1f2d3d;
}

.sub-header p {
  margin: 0;
  line-height: 1.5;
}

.data-table {
  width: 100%;
}

.bool-editor .el-select {
  flex: 1;
}

.dialog-footer {
  display: inline-flex;
  gap: 10px;
}

.dialog-alert {
  margin-bottom: 14px;
}

.dialog-note {
  margin: 2px 0 16px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fbff;
  border: 1px solid #e8eef8;
  color: #5d6b7c;
  font-size: 13px;
  line-height: 1.7;
}

@media (max-width: 1100px) {
  .summary-strip,
  .manage-grid {
    grid-template-columns: 1fr;
  }

  .summary-item.wide {
    grid-column: span 1;
  }
}
</style>
